import { useServices } from '@/app/providers/ServiceContext';
import type { TenantWithBranding, User } from '@yawi/types';
import { ArrowRight, Bell, Calendar, Check, CheckCircle, Eye, EyeOff, Home, Lock, ShieldCheck, Users } from 'lucide-react';
import { useState } from 'react';

interface FamiliarOnboardingProps {
  user: User;
  tenant?: TenantWithBranding | null;
  /** Muestra el carrusel informativo (bienvenida, invitados, avisos). */
  showIntro: boolean;
  /** Agrega el paso final para asignar contraseña propia (cuentas invitadas por magic link). */
  needsPassword: boolean;
  onComplete: () => void;
}

const INFO_STEPS = [
  {
    icon: Users,
    title: 'Bienvenido a tu comunidad',
    description: 'Ahora tienes acceso a las funciones de tu residencial como miembro del hogar.',
  },
  {
    icon: Calendar,
    title: 'Registra visitas',
    description: 'Puedes autorizar invitados para que puedan acceder a tu residencial de forma rápida y segura.',
  },
  {
    icon: Bell,
    title: 'Mantente informado',
    description: 'Recibe avisos importantes, participa en encuestas y consulta el registro de accesos.',
  },
];

type Step = { kind: 'info'; icon: typeof Users; title: string; description: string } | { kind: 'password' };

// Misma regla de fuerza que usa el backend en ResetPasswordDto: sin
// requisito de carácter especial (no existe en ningún lado del codebase).
const isPasswordValid = (pwd: string) =>
  pwd.length >= 8 && /[A-Z]/.test(pwd) && /[a-z]/.test(pwd) && /[0-9]/.test(pwd);

export const FamiliarOnboarding: React.FC<FamiliarOnboardingProps> = ({
  user,
  tenant,
  showIntro,
  needsPassword,
  onComplete,
}) => {
  const { systemSettings, userRepo, refreshCurrentUser } = useServices();
  const [currentStep, setCurrentStep] = useState(0);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [pwError, setPwError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const steps: Step[] = [
    ...(showIntro ? INFO_STEPS.map((s) => ({ kind: 'info' as const, ...s })) : []),
    ...(needsPassword ? [{ kind: 'password' as const }] : []),
  ];

  const branding = {
    appName: tenant?.branding?.primaryColor ? tenant.name : (systemSettings?.branding?.appName || 'Hommi'),
    primaryColor: tenant?.branding?.primaryColor || systemSettings?.branding?.primaryColor || '#8B5CF6',
    logoUrl: tenant?.branding?.logoUrl || systemSettings?.branding?.logoUrl || '',
  };

  const step = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  const handleNext = () => {
    if (!isLastStep) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handleSetPassword = async () => {
    if (!isPasswordValid(password)) {
      setPwError('La contraseña debe tener al menos 8 caracteres, con mayúscula, minúscula y número.');
      return;
    }
    if (password !== confirmPassword) {
      setPwError('Las contraseñas no coinciden.');
      return;
    }
    setPwError('');
    setSubmitting(true);
    try {
      await userRepo.setInitialPassword(password);
      await refreshCurrentUser();
      onComplete();
    } catch (err: unknown) {
      setPwError(err instanceof Error ? err.message : 'No se pudo asignar la contraseña. Intenta de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const requirements = [
    { test: (p: string) => p.length >= 8, label: 'Mínimo 8 caracteres' },
    { test: (p: string) => /[A-Z]/.test(p), label: '1 mayúscula' },
    { test: (p: string) => /[a-z]/.test(p), label: '1 minúscula' },
    { test: (p: string) => /[0-9]/.test(p), label: '1 número' },
  ];

  const CurrentIcon = step.kind === 'info' ? step.icon : Lock;

  return (
    <div className="min-h-screen w-full flex relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-slate-100" />
      <div className="absolute inset-0">
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] bg-purple-300/50 rounded-full blur-[100px]" />
        <div className="absolute top-1/4 -right-32 w-[400px] h-[400px] bg-indigo-300/40 rounded-full blur-[80px]" />
        <div className="absolute -bottom-32 left-1/4 w-[600px] h-[600px] bg-violet-300/40 rounded-full blur-[120px]" />
      </div>

      {/* Content */}
      <div className="w-full flex items-center justify-center relative z-10 px-6 py-8">
        <div
          className="w-full max-w-lg rounded-3xl p-8"
          style={{
            background: 'rgba(255, 255, 255, 0.6)',
            backdropFilter: 'blur(40px) saturate(180%)',
            WebkitBackdropFilter: 'blur(40px) saturate(180%)',
            border: '1px solid rgba(255, 255, 255, 0.8)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div
                className="w-11 h-11 rounded-2xl flex items-center justify-center"
                style={{
                  background: `linear-gradient(135deg, ${branding.primaryColor}, #6366F1)`,
                  boxShadow: `0 4px 16px ${branding.primaryColor}50`,
                }}
              >
                {branding.logoUrl ? (
                  <img src={branding.logoUrl} alt="Logo" className="w-6 h-6 object-contain" />
                ) : (
                  <ShieldCheck className="w-6 h-6 text-white" />
                )}
              </div>
            </div>

            {/* Step indicators */}
            <div className="flex gap-2">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentStep
                      ? 'w-6 bg-purple-600'
                      : index < currentStep
                        ? 'bg-purple-400'
                        : 'bg-slate-300'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* User greeting */}
          <div className="mb-8 p-4 bg-slate-50 rounded-xl border border-slate-100">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-bold text-xl">
                {user.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="text-sm text-slate-500">Hola,</p>
                <p className="text-lg font-bold text-slate-800">{user.name}</p>
                {tenant && (
                  <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                    <Home size={12} /> {tenant.name}
                  </p>
                )}
              </div>
            </div>
          </div>

          {step.kind === 'info' ? (
            <>
              {/* Step content */}
              <div className="text-center mb-8">
                <div
                  className="w-20 h-20 mx-auto rounded-2xl flex items-center justify-center mb-6"
                  style={{
                    background: `linear-gradient(135deg, ${branding.primaryColor}20, ${branding.primaryColor}30)`,
                    border: `1px solid ${branding.primaryColor}30`,
                  }}
                >
                  <CurrentIcon className="w-10 h-10" style={{ color: branding.primaryColor }} />
                </div>
                <h2 className="text-2xl font-bold text-slate-800 mb-3">{step.title}</h2>
                <p className="text-slate-500 leading-relaxed">{step.description}</p>
              </div>

              {/* Features for FAMILIAR role */}
              {isLastStep && (
                <div className="mb-8 space-y-3">
                  <p className="text-sm font-medium text-slate-700 mb-3">Lo que puedes hacer:</p>
                  {[
                    'Registrar y gestionar invitados',
                    'Ver comunicados de la comunidad',
                    'Participar en encuestas',
                    'Consultar historial de accesos',
                  ].map((feature, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-emerald-50 rounded-xl border border-emerald-100">
                      <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
                      <span className="text-sm text-slate-700">{feature}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Action button */}
              <button
                onClick={handleNext}
                className="w-full py-4 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-[0.98]"
                style={{
                  background: `linear-gradient(135deg, ${branding.primaryColor}, #6366F1)`,
                  boxShadow: `0 4px 16px ${branding.primaryColor}50`,
                }}
              >
                {!isLastStep ? (
                  <>
                    Siguiente
                    <ArrowRight size={18} />
                  </>
                ) : (
                  <>
                    Comenzar
                    <ArrowRight size={18} />
                  </>
                )}
              </button>

              {/* Skip button */}
              {!isLastStep && (
                <button
                  onClick={onComplete}
                  className="w-full mt-3 py-2 text-sm text-slate-500 hover:text-slate-700 transition-colors"
                >
                  Omitir introducción
                </button>
              )}
            </>
          ) : (
            <>
              {/* Password step */}
              <div className="text-center mb-6">
                <div
                  className="w-20 h-20 mx-auto rounded-2xl flex items-center justify-center mb-6"
                  style={{
                    background: `linear-gradient(135deg, ${branding.primaryColor}20, ${branding.primaryColor}30)`,
                    border: `1px solid ${branding.primaryColor}30`,
                  }}
                >
                  <Lock className="w-10 h-10" style={{ color: branding.primaryColor }} />
                </div>
                <h2 className="text-2xl font-bold text-slate-800 mb-3">Crea tu contraseña</h2>
                <p className="text-slate-500 leading-relaxed">
                  Entraste con un enlace mágico. Asigna una contraseña propia para poder iniciar sesión también con tu correo y contraseña.
                </p>
              </div>

              <div className="space-y-4 mb-2 text-left">
                <div className="relative">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Contraseña</label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setPwError(''); }}
                    placeholder="Mínimo 8 caracteres"
                    className="w-full rounded-xl px-4 py-3 pr-11 text-sm text-slate-800 placeholder:text-slate-400 outline-none border border-slate-200 focus:border-purple-400 transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-3 top-9 text-slate-400 hover:text-slate-600"
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>

                {password.length > 0 && (
                  <div className="grid grid-cols-2 gap-1">
                    {requirements.map((req, i) => {
                      const ok = req.test(password);
                      return (
                        <span key={i} className={`text-[11px] flex items-center gap-1 ${ok ? 'text-emerald-600' : 'text-slate-400'}`}>
                          {ok ? <Check size={10} /> : <span className="w-2.5" />} {req.label}
                        </span>
                      );
                    })}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Confirmar contraseña</label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); setPwError(''); }}
                    placeholder="Repite la contraseña"
                    className="w-full rounded-xl px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 outline-none border border-slate-200 focus:border-purple-400 transition-colors"
                  />
                </div>

                {pwError && <p className="text-sm text-red-600">{pwError}</p>}
              </div>

              <button
                onClick={handleSetPassword}
                disabled={submitting}
                className="w-full py-4 mt-4 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
                style={{
                  background: `linear-gradient(135deg, ${branding.primaryColor}, #6366F1)`,
                  boxShadow: `0 4px 16px ${branding.primaryColor}50`,
                }}
              >
                {submitting ? (
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    Guardar contraseña
                    <ArrowRight size={18} />
                  </>
                )}
              </button>

              <button
                onClick={onComplete}
                disabled={submitting}
                className="w-full mt-3 py-2 text-sm text-slate-500 hover:text-slate-700 transition-colors"
              >
                Ahora no, recuérdamelo después
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
